import type { ViewModeGeneralSettingsBuf } from "~/classes/generated/interface"

import { ViewModeColumnViewSettingsBuf, ViewModeDetailViewSettingsBuf, ViewModeFilterBuf, ViewModeGalleryViewSettingsBuf, ViewModeSettingsBuf, ViewModeShowBuf, ViewModeSortBuf, ViewModeTableViewSettingsBuf } from "~/classes/generated/interface"

import { parseVector } from "./datagram"
import { addUserPropertyIdToBuf, parseUserPropertyId } from "./id"

export function addViewModeShowToBuf(fbb: DatagramBufferBuilder, show: InterfaceViewModeShow) {
  const propertyId = addUserPropertyIdToBuf(fbb, show.propertyId)

  ViewModeShowBuf.startViewModeShowBuf(fbb)
  ViewModeShowBuf.addPropertyId(fbb, propertyId)
  ViewModeShowBuf.addColor(fbb, show.color)
  ViewModeShowBuf.addSize(fbb, show.size)
  if (show.scale)
    ViewModeShowBuf.addScale(fbb, show.scale)
  return ViewModeShowBuf.endViewModeShowBuf(fbb)
}

export function addViewModeSortToBuf(fbb: DatagramBufferBuilder, sort: InterfaceViewModeSort) {
  const propertyId = addUserPropertyIdToBuf(fbb, sort.propertyId)
  ViewModeSortBuf.startViewModeSortBuf(fbb)
  ViewModeSortBuf.addPropertyId(fbb, propertyId)
  ViewModeSortBuf.addDescending(fbb, sort.descending)
  return ViewModeSortBuf.endViewModeSortBuf(fbb)
}

export function addViewModeFilterToBuf(fbb: DatagramBufferBuilder, filter: InterfaceViewModeFilter) {
  const propertyId = addUserPropertyIdToBuf(fbb, filter.propertyId)
  let value
  if ("value" in filter)
    value = fbb.createString(filter.value)
  ViewModeFilterBuf.startViewModeFilterBuf(fbb)
  ViewModeFilterBuf.addPropertyId(fbb, propertyId)
  ViewModeFilterBuf.addType(fbb, filter.type)
  if (isDefined(value))
    ViewModeFilterBuf.addValue(fbb, value)
  return ViewModeFilterBuf.endViewModeFilterBuf(fbb)
}

export function addViewModeColumnViewSettingsToBuf(fbb: DatagramBufferBuilder, settings: InterfaceViewModeColumnViewSettings) {
  const columns = ViewModeColumnViewSettingsBuf.createColumnsVector(fbb, settings.columns)
  ViewModeColumnViewSettingsBuf.startViewModeColumnViewSettingsBuf(fbb)
  ViewModeColumnViewSettingsBuf.addColumns(fbb, columns)
  return ViewModeColumnViewSettingsBuf.endViewModeColumnViewSettingsBuf(fbb)
}

export function addViewModeDetailViewSettingsToBuf(fbb: DatagramBufferBuilder, settings: InterfaceViewModeDetailViewSettings) {
  ViewModeDetailViewSettingsBuf.startViewModeDetailViewSettingsBuf(fbb)
  ViewModeDetailViewSettingsBuf.addMaxColumnWidth(fbb, settings.maxColumnWidth)
  return ViewModeDetailViewSettingsBuf.endViewModeDetailViewSettingsBuf(fbb)
}

export function addViewModeTableViewSettingsToBuf(fbb: DatagramBufferBuilder, settings: InterfaceViewModeTableViewSettings) {
  const widths = ViewModeTableViewSettingsBuf.createWidthsVector(fbb, settings.widths)
  ViewModeTableViewSettingsBuf.startViewModeTableViewSettingsBuf(fbb)
  ViewModeTableViewSettingsBuf.addWidths(fbb, widths)
  return ViewModeTableViewSettingsBuf.endViewModeTableViewSettingsBuf(fbb)
}

export function addViewModeGalleryViewSettingsToBuf(fbb: DatagramBufferBuilder, settings: InterfaceViewModeGalleryViewSettings) {
  ViewModeGalleryViewSettingsBuf.startViewModeGalleryViewSettingsBuf(fbb)
  ViewModeGalleryViewSettingsBuf.addFilmstrip(fbb, settings.filmstrip)
  ViewModeGalleryViewSettingsBuf.addFilmstripHeight(fbb, settings.filmstripHeight)
  ViewModeGalleryViewSettingsBuf.addProperties(fbb, settings.properties)
  return ViewModeGalleryViewSettingsBuf.endViewModeGalleryViewSettingsBuf(fbb)
}

export function addViewModeSettingsToBuf(
  fbb: DatagramBufferBuilder,
  settings: InterfaceViewModeSettings,
) {
  const show = ViewModeSettingsBuf.createShowVector(fbb, settings.show.map(s => addViewModeShowToBuf(fbb, s)))
  const sort = ViewModeSettingsBuf.createSortVector(fbb, settings.sort.map(s => addViewModeSortToBuf(fbb, s)))
  const filter = ViewModeSettingsBuf.createFilterVector(fbb, settings.filter.map(f => addViewModeFilterToBuf(fbb, f)))
  const column = addViewModeColumnViewSettingsToBuf(fbb, settings.columnView)
  const detail = addViewModeDetailViewSettingsToBuf(fbb, settings.detailView)
  const table = addViewModeTableViewSettingsToBuf(fbb, settings.tableView)
  const gallery = addViewModeGalleryViewSettingsToBuf(fbb, settings.galleryView)

  ViewModeSettingsBuf.startViewModeSettingsBuf(fbb)
  ViewModeSettingsBuf.addMode(fbb, settings.mode)
  ViewModeSettingsBuf.addShow(fbb, show)
  ViewModeSettingsBuf.addSort(fbb, sort)
  ViewModeSettingsBuf.addFilter(fbb, filter)
  ViewModeSettingsBuf.addColumnView(fbb, column)
  ViewModeSettingsBuf.addDetailView(fbb, detail)
  ViewModeSettingsBuf.addTableView(fbb, table)
  ViewModeSettingsBuf.addGalleryView(fbb, gallery)
  return ViewModeSettingsBuf.endViewModeSettingsBuf(fbb)
}

export function parseViewModeColumnViewSettings(buf: ViewModeColumnViewSettingsBuf): InterfaceViewModeColumnViewSettings {
  const columns = parseVector(i => buf.columns(i)!, buf.columnsLength())
  return { columns }
}

export function defaultColumnViewSettings(): InterfaceViewModeColumnViewSettings {
  return {
    columns: [280],
  }
}

export function parseViewModeDetailViewSettings(buf: ViewModeDetailViewSettingsBuf): InterfaceViewModeDetailViewSettings {
  return { maxColumnWidth: buf.maxColumnWidth() }
}

export function parseViewModeTableViewSettings(buf: ViewModeTableViewSettingsBuf): InterfaceViewModeTableViewSettings {
  const widths = parseVector(i => buf.widths(i)!, buf.widthsLength())
  return { widths }
}

export function defaultTableViewSettings(): InterfaceViewModeTableViewSettings {
  return {
    widths: [32, 200, 100],
  }
}

export function parseViewModeGalleryViewSettings(buf: ViewModeGalleryViewSettingsBuf | null): InterfaceViewModeGalleryViewSettings {
  const defaults = defaultGalleryViewSettings()
  return {
    filmstrip: buf?.filmstrip() ?? defaults.filmstrip,
    filmstripHeight: buf?.filmstripHeight() ?? defaults.filmstripHeight,
    properties: buf?.properties() ?? defaults.properties,
  }
}

export function defaultGalleryViewSettings(): InterfaceViewModeGalleryViewSettings {
  return {
    filmstrip: true,
    filmstripHeight: 100,
    properties: true,
  }
}

export function parseViewModeShow(buf: ViewModeShowBuf): InterfaceViewModeShow {
  return {
    propertyId: parseUserPropertyId(buf.propertyId()!),
    size: buf.size(),
    color: buf.color(),
    scale: buf.scale(),
  }
}

export function parseViewModeSort(buf: ViewModeSortBuf): InterfaceViewModeSort {
  return {
    propertyId: parseUserPropertyId(buf.propertyId()!),
    descending: buf.descending(),
  }
}

export function parseViewModeFilter(buf: ViewModeFilterBuf) {
  const propertyId = parseUserPropertyId(buf.propertyId()!)
  const type = buf.type()
  const value = buf.value() ?? undefined
  return { propertyId, type, value } as InterfaceViewModeFilter
}

export function parseViewModeGeneralSettings(buf: ViewModeGeneralSettingsBuf | null) {
  const notes = buf ? buf.notes() : true // notes defaults to true
  return { notes }
}

export function parseViewModeSettings(buf: ViewModeSettingsBuf): InterfaceViewModeSettings {
  const mode = buf.mode()
  const show = parseVector(i => parseViewModeShow(buf.show(i)!), buf.showLength())
  const sort = parseVector(i => parseViewModeSort(buf.sort(i)!), buf.sortLength())
  const filter = parseVector(i => parseViewModeFilter(buf.filter(i)!), buf.filterLength())
  const general = parseViewModeGeneralSettings(buf.general())
  const columnView = parseViewModeColumnViewSettings(buf.columnView()!)
  const detailView = parseViewModeDetailViewSettings(buf.detailView()!)
  const tableView = parseViewModeTableViewSettings(buf.tableView()!)
  const galleryView = parseViewModeGalleryViewSettings(buf.galleryView())
  return { mode, show, sort, filter, columnView, detailView, tableView, galleryView, general }
}
